import { Box, Divider, Group, Stack } from '@mantine/core';
import Text from '@ui/components/Text/Text';
import WhiteBox from 'src/components/WhiteBox/WhiteBox';
import useSnackbarSystem from '@ui/systems/useSnackbarSystem';
import useNonMembersQuery from '../../queries/useNonMembersQuery';
import useRequestSeatForNonMemberMutation from '../../queries/useRequestSeatForNonMemberMutation';
import NonMemberRow from './NonMemberRow';

type Props = {
	workspaceID: number;
	isOwner: boolean;
	onMemberAdded: () => void;
};

const NonMembersSection = ( { workspaceID, isOwner, onMemberAdded }: Props ) => {
	const { data: nonMembers } = useNonMembersQuery( workspaceID );
	const snackbar = useSnackbarSystem();
	const requestSeatMutation = useRequestSeatForNonMemberMutation( {
		workspaceID,
		onSuccess: () => snackbar.showOutlinedSuccessMessage( {
			title: 'Request sent',
			information: 'Your account owner will be notified.'
		} ),
		onError: () => snackbar.showErrorMessage( {
			title: 'Something went wrong. Please try again.'
		} )
	} );

	if ( !nonMembers || nonMembers.length === 0 ) return null;

	return (
		<WhiteBox className="NonMembersSection">
			<Stack spacing={4}>
				<Text variant="h4">Non Members</Text>
				<Text
					variant="body"
					style={{ color: 'var(--color-text-secondary)', lineHeight: '22px', letterSpacing: 0 }}
				>
					These users have a free 30-day grace period. After that, some editor permissions like
					managing assets and adding rounds will no longer be available.
					Assign a workspace seat for full access.
				</Text>
			</Stack>
			<Group spacing={2} w="100%" position="apart" noWrap className="column-names">
				<Box style={{ flex: 1 }}>Non Members</Box>
				<Box w="180px" style={{ paddingLeft: 30 }}>Status</Box>
				<Box w="120px">Last active</Box>
				<Box w="120px">Collaborations</Box>
				<Box w="120px">Actions</Box>
			</Group>
			<Divider color="gray.2" w="100%" h="1px" mt={10} mb={12} />
			<Stack spacing={16} w="100%">
				{nonMembers.map( nonMember => (
					<NonMemberRow
						key={nonMember.id}
						workspaceID={workspaceID}
						nonMember={nonMember}
						isOwner={isOwner}
						onMemberAdded={onMemberAdded}
						onRequestSeat={requestSeatMutation.mutate}
						isRequestingSeat={requestSeatMutation.isLoading}
					/>
				) )}
			</Stack>
		</WhiteBox>
	);
};

export default NonMembersSection;
